import * as React from "react";
import {
  Column,
  Columns,
  Stack,
  Hidden,
  Divider,
} from "@origin-digital/ods-core";
import { useParams, useHistory } from "react-router-dom";
import { logger } from "@origin-digital/reporting-client";
import { ExperienceProvider } from "@origin-digital/features";
import { findLabel, SearchLevel } from "../helpers/mapping";
import { IAppContext } from "../AppContext";
import { AccordionController } from "../components/Accordion";
import { LoginViewMessagesController } from "../components/LoginViewMessages";
import { Loading } from "../components/Loading";
import { ILabel } from "../../../shared/interface/label";
import { routes } from "../helpers/router.helper";
import { UseParamsType } from "../router";
import { IMetaData } from "../../../shared/interface/article";
import { ScreenTemplate } from "./ScreenTemplate";

interface IProps {
  context: IAppContext;
}

export const Category = ({ context }: IProps) => {
  const { navigation, isNative } = context;
  const { category } = useParams() as UseParamsType;
  const history = useHistory();
  const categories = navigation.data ? navigation.data.categories : [];
  const data = findLabel(categories, category, SearchLevel.CATEGORY);

  if (!data && navigation.data) {
    logger.warn(`[Category] cannot find category: "${category}"`, {
      navigationData: navigation.data,
    });
    history.replace(routes.home());
  }

  const title = data ? data.displayName : "";
  const description = data ? data.metaDesc : "";

  const categoryObj = categories.find((x) => x.name === category);
  const categoryName = categoryObj ? categoryObj.name : "";
  const subCategories = categoryObj ? categoryObj.children : [];

  const metaData: IMetaData = {
    metaTitle: data?.metaTitle,
    metaDescription: data?.metaDesc,
  };

  return (
    <ScreenTemplate
      title={title}
      description={description}
      isNative={isNative}
      data-id="category"
      metaData={metaData}
      labelId={data?.labelId}
    >
      <Stack space={["xlarge", "xxlarge"]}>
        <Stack space="none">
          <Columns collapseBelow="lg" space="xlarge">
            <ExperienceProvider mboxes={["OE-518-mbox"]}>
              <AccordionsColumn
                subCategories={subCategories || []}
                categoryName={categoryName}
                isNative={isNative}
                loading={navigation.loading}
              />
            </ExperienceProvider>
          </Columns>
        </Stack>
      </Stack>
    </ScreenTemplate>
  );
};

const LoadingIndicator = () => (
  <>
    <Hidden above="sm">
      <Loading size="sm" />
    </Hidden>
    <Hidden below="md">
      <Loading size="md" />
    </Hidden>
  </>
);

interface IAccordionsProps {
  subCategories: ILabel[];
  categoryName: string;
  isNative: boolean;
  loading: boolean;
}

const AccordionsColumn = ({
  subCategories,
  categoryName,
  loading,
  isNative,
}: IAccordionsProps) => {
  return (
    <Column width={loading ? "flex" : "2/3"}>
      <Stack space="none">
        {loading ? (
          <LoadingIndicator />
        ) : (
          <AccordionController
            subCategories={subCategories}
            category={categoryName}
          />
        )}
        {!isNative && (
          <Hidden above="md">
            <Stack space={["large", "xlarge"]}>
              <LoginViewMessagesController />
              <Divider />
            </Stack>
          </Hidden>
        )}
      </Stack>
    </Column>
  );
};
